import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ROLE_META } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Lock, Shield, ArrowRight, Eye, EyeOff, Mail, AlertCircle } from "lucide-react";
import { InstallAppButton } from "../components/InstallAppButton";

const NavLinks = ({ onPickRole }) => {
  const items = [
    { key: "login", label: "Login" },
    { key: "receptionist", label: "Reception" },
    { key: "doctor", label: "Doctor" },
    { key: "nurse", label: "Nurse" },
    { key: "lab_tech", label: "Lab" },
    { key: "pharmacist", label: "Pharmacy" },
    { key: "hospital_admin", label: "Admin" },
  ];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {items.map((it) => {
        const active = it.key === "login";
        return (
          <button
            key={it.key}
            data-testid={`top-nav-${it.key}`}
            onClick={() => onPickRole(it.key)}
            className={`px-3 py-1.5 text-[13px] rounded-md transition-colors ${active ? "bg-[#1976D2] text-white" : "text-slate-300 hover:text-white hover:bg-white/5"}`}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
};

export default function Login() {
  const navigate = useNavigate();
  const { user, loginGoogle, loginPassword } = useAuth();
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      const home = ROLE_META[user.role]?.home || "/app";
      navigate(home, { replace: true });
    }
  }, [user, navigate]);

  const onPasswordSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email || !password) { setError("Enter your email and password"); return; }
    setBusy(true);
    try {
      const u = await loginPassword(email, password);
      if (u?.role === "patient") {
        setError("Patient sign-in has been removed. Please contact your facility for your records.");
        setBusy(false);
        return;
      }
      toast.success(`Welcome back, ${u.name}`);
      const home = ROLE_META[u?.role]?.home || "/app";
      navigate(home);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      const nice = typeof msg === "string" ? msg : (Array.isArray(msg) ? msg.map((m) => m.msg).join(" ") : "Sign-in failed");
      setError(nice);
    } finally { setBusy(false); }
  };

  const onTopNavPick = (key) => {
    if (key === "login") return;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0a1628]" data-testid="login-page">
      {/* Top strip */}
      <div className="ths-top-strip border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] tracking-[0.22em] font-semibold text-slate-300" data-testid="brand-strip">
            VITALITY MEDICAL SOFTWARE
          </div>
          <div className="flex items-center gap-3">
            <InstallAppButton />
            <NavLinks onPickRole={onTopNavPick} />
          </div>
        </div>
      </div>

      {/* Split panels */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        {/* LEFT — Navy brand */}
        <div className="ths-navy-panel p-8 lg:p-14 xl:p-20 text-white flex flex-col">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden shadow-lg ring-1 ring-white/10">
              <img src="/vms-logo.png" alt="VMS" className="w-full h-full object-cover" />
            </div>
            <div className="text-[15px] font-semibold tracking-tight">Vitality Medical Software</div>
          </div>

          <div className="flex-1 flex flex-col justify-center py-14 max-w-xl">
            <h1 className="text-4xl sm:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight text-white">
              Connecting Healthcare.<br/>Empowering Lives.
            </h1>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">
              One secure record for every patient — accessible at any VMS hospital, clinic, laboratory or health centre across Uganda with proper authorization.
            </p>

            <div className="mt-12 grid grid-cols-3 gap-8 max-w-lg">
              <div>
                <div className="stat-number text-3xl">135</div>
                <div className="text-[12px] text-slate-400 mt-1">Facilities live</div>
              </div>
              <div>
                <div className="stat-number text-3xl">412K+</div>
                <div className="text-[12px] text-slate-400 mt-1">Patient records</div>
              </div>
              <div>
                <div className="stat-number text-3xl">99.98%</div>
                <div className="text-[12px] text-slate-400 mt-1">Uptime</div>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-6 text-[11px] text-slate-400 flex-wrap">
              <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> AES-256 at rest</span>
              <span>TLS 1.3 in transit</span>
              <span>2FA enforced</span>
              <span>Uganda DPPA compliant</span>
            </div>
          </div>

          <div className="mt-auto flex items-center gap-4">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-32 h-32 flex-shrink-0">
              <img src="/vms-logo.png" alt="Vitality Medical Software" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">vitalitymedicalsoftware.com</div>
              <div className="text-[12px] text-slate-400">One number. Every facility.</div>
            </div>
          </div>
        </div>

        {/* RIGHT — Sign in card */}
        <div className="bg-slate-50 p-6 lg:p-14 xl:p-20 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
              <h2 className="text-[22px] font-semibold text-slate-900 tracking-tight">Staff &amp; facility sign in</h2>
              <p className="text-[13px] text-slate-500 mt-1">For hospitals, clinics, laboratories and health centres</p>

              <div className="mt-6 space-y-4">
                <PasswordForm
                  email={email} setEmail={setEmail}
                  password={password} setPassword={setPassword}
                  showPwd={showPwd} setShowPwd={setShowPwd}
                  error={error}
                  busy={busy}
                  onSubmit={onPasswordSubmit}
                />

                <div className="relative py-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center"><span className="bg-white px-3 text-[11px] uppercase tracking-wider text-slate-400">Or continue with</span></div>
                </div>

                <button
                  data-testid="enterprise-google-btn"
                  onClick={loginGoogle}
                  className="w-full h-11 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center gap-2.5 text-sm font-medium text-slate-700 transition-colors"
                >
                  <GoogleIcon /> Google
                </button>

                <div className="flex items-center gap-2 pt-1">
                  <input id="remember" data-testid="remember-cb" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#1976D2]" />
                  <label htmlFor="remember" className="text-[13px] text-slate-600">Keep me signed in on this workstation</label>
                </div>
              </div>
            </div>

            <div className="text-center mt-6 text-[13px] text-slate-500">
              New facility? <Link to="/" className="text-[#1976D2] font-medium hover:underline" data-testid="start-trial-link">Start your 1-month free trial</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.5 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.5-.2-3-.4-4.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-2 13.4-5.3l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.5 41 16.2 45.5 24 45.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.2 0-1.5-.2-3-.4-4z"/>
    </svg>
  );
}

function PasswordForm({ email, setEmail, password, setPassword, showPwd, setShowPwd, error, busy, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="password-form">
      <div>
        <label className="block text-[11px] font-semibold tracking-wider text-slate-500 uppercase mb-1.5">Work email or Staff ID</label>
        <div className="relative">
          <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="pwd-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="e.g. e.kaggwa@stmarys.ths.ug"
            autoComplete="email"
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-[11px] font-semibold tracking-wider text-slate-500 uppercase">Password</label>
          <button type="button" data-testid="forgot-link" onClick={() => toast.info("Password reset — coming soon. Contact your facility admin.")} className="text-[11px] text-[#1565C0] hover:underline font-medium">Forgot password?</button>
        </div>
        <div className="relative">
          <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="pwd-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPwd ? "text" : "password"}
            placeholder="Enter your password"
            autoComplete="current-password"
            className="w-full h-11 pl-9 pr-11 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
          />
          <button
            type="button"
            data-testid="pwd-toggle"
            onClick={() => setShowPwd((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
            aria-label={showPwd ? "Hide password" : "Show password"}
          >{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        </div>
      </div>
      {error && (
        <div className="rounded-lg bg-[#fdecec] border border-[#f5c2c2] text-[#B71C1C] text-[13px] px-3 py-2 flex items-start gap-2" data-testid="pwd-error">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{error}</span>
        </div>
      )}
      <button
        type="submit"
        data-testid="pwd-submit"
        disabled={busy}
        className="w-full h-11 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60 transition-colors"
      >{busy ? "Please wait…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}</button>
    </form>
  );
}
